import { Component } from '@theme/component';
import { isLowPowerDevice, prefersReducedMotion, yieldToMainThread } from '@theme/utilities';

/**
 * <r-media> — Rocky media framework (WS9).
 *
 * One element serves both responsive images and Shopify-hosted ambient video:
 *   - viewport-driven autoplay/pause for muted looping video, with near-viewport
 *     source promotion (sources ship as `data-r-src`, `preload="none"`);
 *   - the entrance effect (`data-r-effect`: fade-rise) — gating is JS-armed,
 *     the motion itself lives in CSS inside
 *     `@media (prefers-reduced-motion: no-preference)`. Parallax is no longer
 *     an r-media effect: WS18 moved it onto the host element as a whole-plane
 *     drift (`data-r-parallax`, R-PARALLAX band in r-base.css + r-parallax.js);
 *   - the WCAG 2.2.2 pause/play/replay control for all auto-playing media.
 *
 * Playback state machine, owned here and mirrored to `data-r-state`:
 *   idle → ready → playing ⇄ paused, plus blocked / static / ended.
 * Every visual end-state derives from the attribute alone — transitions are
 * decorative, so a frozen background tab can never strand the UI mid-state.
 *
 * Deliberately NOT integrated with MediaStartedPlayingEvent: r-media only ever
 * plays muted ambient loops, which cannot conflict with audio from a
 * user-initiated `deferred-media` video (PDP, video block). Dispatching the
 * event would wrongly pause those. Non-interference is the integration.
 *
 * Rendered exclusively by snippets/r-media.liquid — see that file for the
 * markup contract, and .cursor/references/r-media-framework.md for usage.
 *
 * @typedef {Object} Refs
 * @property {HTMLImageElement} [poster] - responsive poster/still image
 * @property {HTMLVideoElement} [video] - ambient video (video variant only)
 * @property {HTMLSourceElement[]} [sources] - video sources carrying data-r-src, and
 *   optionally data-r-media for the art-directed desktop/mobile pair
 * @property {HTMLButtonElement} [toggle] - play/pause/replay control
 * @extends {Component<Refs>}
 */

/** Ratio of the element that must be visible before video plays. */
const PLAY_RATIO = 0.3;

/** Fraction of the viewport a tall element must cover to count as playable. */
const VIEWPORT_COVER_RATIO = 0.5;

/** How far outside the viewport video sources are promoted and loaded. */
const NEAR_MARGIN = '400px 0px';

/** Concurrent playing videos — iOS compositor/memory backstop. */
const MAX_PLAYING = isLowPowerDevice() ? 1 : 2;

/** All connected instances (visibilitychange / bfcache / preference resync). */
const instances = new Set();

/** Currently playing instances in FIFO order (Set preserves insertion order). */
const playing = new Set();

/** Promotes video sources and loads metadata just before the media is needed. */
const nearObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      if (entry.target instanceof RMedia) entry.target.prepare();
      nearObserver.unobserve(entry.target);
    }
  },
  { rootMargin: NEAR_MARGIN }
);

/* ↗ .cursor/references/code-notes/r-media.js.md#§1 */
const viewObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.target instanceof RMedia) entry.target.onViewChange(entry);
    }
  },
  { threshold: [0, 0.1, 0.2, PLAY_RATIO] }
);

class RMedia extends Component {
  /** True after the user explicitly paused — blocks every auto-resume path. */
  #userPaused = false;

  /** True after the user explicitly played — lets viewport re-entry resume a
   *  video that the automatic ladder (reduced motion, save-data) would skip. */
  #userActivated = false;

  /** Guards overlapping play() promises. */
  #playPending = false;

  /** Latest any-intersection state from the view observer. */
  #inView = false;

  /** Latest "visible enough to play" state (with exit hysteresis: playback
   *  only stops once the element fully leaves the viewport). */
  #playEligible = false;

  /** @type {AbortController | undefined} */
  #listeners;

  /** Elements already wired with listeners — a morph may keep the same nodes,
   *  and re-running the init methods must not stack duplicates. */
  #wiredVideo = null;

  #wiredPoster = null;

  /** Breakpoint listeners for art-directed video are wired once per connection. */
  #wiredArtDirection = false;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners = new AbortController();
    instances.add(this);

    this.#arm();
    this.#initVideo();
    this.#initBlurUp();

    viewObserver.observe(this);
    if (this.refs.video) nearObserver.observe(this);
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    // The flag tracks "attached to the CURRENT controller" — aborting it means a
    // reconnect must wire the breakpoint listeners again.
    this.#wiredArtDirection = false;
    viewObserver.unobserve(this);
    nearObserver.unobserve(this);
    instances.delete(this);
    playing.delete(this);
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    // A section-renderer morph may have reset server-rendered attributes and
    // swapped subtree nodes; re-derive everything from the DOM.
    this.#arm();
    this.#initVideo();
    this.#initBlurUp();
    this.resync();
  }

  /**
   * Declarative `on:click="/toggleMotion"` handler for the control button.
   * Videos: pause / play / replay.
   * @param {Event} [event]
   */
  toggleMotion(event) {
    event?.preventDefault();

    const { video } = this.refs;
    if (!video) {
      this.toggleAttribute('data-r-motion-paused');
      return;
    }

    if (this.dataset.rState === 'playing') {
      this.#userPaused = true;
      this.#pause();
      return;
    }

    this.#userPaused = false;
    this.#userActivated = true;
    if (this.dataset.rState === 'ended') video.currentTime = 0;
    this.#tryPlay();
  }

  /** Promote `data-r-src` → `src` and load. Idempotent and morph-safe: reads
   *  the live DOM instead of trusting a flag. */
  prepare() {
    const { video, sources = [] } = this.refs;
    if (!video) return;

    // `static` promises zero video bytes (save-data, reduced motion, or
    // autoplay off) until the user explicitly presses play.
    if (this.dataset.rState === 'static' && !this.#userActivated) return;

    let promoted = false;
    for (const source of sources) {
      // ↗ .cursor/references/code-notes/r-media.js.md#§2
      const query = source.dataset.rMedia;
      if (query && !matchMedia(query).matches) {
        source.removeAttribute('src');
        continue;
      }
      if (source.getAttribute('src') || !source.dataset.rSrc) continue;
      source.src = source.dataset.rSrc;
      promoted = true;
    }

    if (promoted) {
      video.load();
      if (this.dataset.rState === 'idle') this.#setState('ready');
    }
  }

  /**
   * View-observer callback.
   * @param {IntersectionObserverEntry} entry
   */
  onViewChange(entry) {
    const rootHeight = entry.rootBounds?.height || window.innerHeight;
    const coversViewport = entry.intersectionRect.height >= rootHeight * VIEWPORT_COVER_RATIO;

    this.#inView = entry.isIntersecting;
    this.#playEligible = entry.isIntersecting && (entry.intersectionRatio >= PLAY_RATIO || coversViewport);
    this.toggleAttribute('data-r-in-view', this.#inView);

    if (this.#inView) {
      this.#showEntrance();
    } else if (this.hasAttribute('data-r-effect-repeat')) {
      this.removeAttribute('data-r-visible');
    }

    this.resync();
  }

  /** Re-evaluate playback from the last known view state. Called on view
   *  changes, visibility/bfcache restores, and preference changes. */
  resync() {
    this.#syncPlayback();
  }

  /** Pause without touching user intent — for visibilitychange and the
   *  concurrency cap. Viewport re-entry or tab return resumes automatically. */
  systemPause() {
    this.#pause();
  }

  #hasEffect(token) {
    return (this.dataset.rEffect ?? '').split(/\s+/).includes(token);
  }

  /* ↗ .cursor/references/code-notes/r-media.js.md#§3 */
  #arm() {
    const armed = Boolean(this.dataset.rEffect) && !prefersReducedMotion();
    this.toggleAttribute('data-r-armed', armed);

    // Videos are the only motion that needs a pause control now: the one still-image
    // effect that ran on its own (ken-burns) is retired.
    const needsToggle = Boolean(this.refs.video);
    if (needsToggle) this.refs.toggle?.removeAttribute('hidden');
  }

  #initVideo() {
    const { video } = this.refs;
    if (!video || !this.#listeners) return;

    if (!this.dataset.rState) this.#setState('idle');

    // The automatic ladder's terminal "no video bytes" state. Re-derived on
    // every init because a morph resets the attribute to the server's "idle".
    if (!this.#shouldAutoplay() && !this.#userActivated && this.dataset.rState === 'idle') {
      this.#setState('static');
    }

    if (video === this.#wiredVideo) return;
    this.#wiredVideo = video;

    video.addEventListener(
      'ended',
      () => {
        playing.delete(this);
        this.#setState('ended');
      },
      { signal: this.#listeners.signal }
    );

    this.#initArtDirection();
  }

  /* ↗ .cursor/references/code-notes/r-media.js.md#§4 */
  #initArtDirection() {
    const { sources = [] } = this.refs;
    if (!this.#listeners || this.#wiredArtDirection) return;

    const queries = new Set(sources.map((source) => source.dataset.rMedia).filter(Boolean));
    if (!queries.size) return;

    // ↗ .cursor/references/code-notes/r-media.js.md#§5
    this.#wiredArtDirection = true;

    for (const query of queries) {
      matchMedia(query).addEventListener('change', () => this.#reselectSource(), {
        signal: this.#listeners.signal,
      });
    }
  }

  /** Re-run source selection after a breakpoint crossing, but only once bytes
   *  have actually been committed — before that, `prepare()` picks correctly. */
  #reselectSource() {
    const { video, sources = [] } = this.refs;
    if (!video || !sources.some((source) => source.getAttribute('src'))) return;

    const wasPlaying = this.dataset.rState === 'playing';
    for (const source of sources) source.removeAttribute('src');
    this.prepare();
    if (!wasPlaying) return;

    // ↗ .cursor/references/code-notes/r-media.js.md#§6
    video.addEventListener('loadeddata', () => this.#tryPlay(), {
      once: true,
      signal: this.#listeners?.signal,
    });
  }

  #initBlurUp() {
    const { poster } = this.refs;
    if (!poster || !this.#hasEffect('blur-up') || !this.#listeners) return;

    if (poster.complete) {
      this.setAttribute('data-r-loaded', '');
      return;
    }

    if (poster === this.#wiredPoster) return;
    this.#wiredPoster = poster;

    const reveal = () => this.setAttribute('data-r-loaded', '');
    poster.addEventListener('load', reveal, { once: true, signal: this.#listeners.signal });
    // Fail open — never hold the layer invisible behind a broken image.
    poster.addEventListener('error', reveal, { once: true, signal: this.#listeners.signal });
  }

  #showEntrance() {
    if (!this.hasAttribute('data-r-armed') || this.hasAttribute('data-r-visible')) return;
    // Let layout settle before flipping the transition class (jumbo-text pattern).
    yieldToMainThread().then(() => {
      if (this.#inView) this.setAttribute('data-r-visible', '');
    });
  }

  /** Automatic-playback eligibility. User activation is handled separately. */
  #shouldAutoplay() {
    if (!this.hasAttribute('data-r-autoplay')) return false;
    if (prefersReducedMotion()) return false;
    if (navigator.connection?.saveData) return false;
    if (matchMedia('(prefers-reduced-data: reduce)').matches) return false;
    return true;
  }

  #syncPlayback() {
    const { video } = this.refs;
    if (!video || document.hidden) return;

    const wantsPlay = (this.#shouldAutoplay() || this.#userActivated) && !this.#userPaused;

    if (this.#playEligible && wantsPlay && this.dataset.rState !== 'ended') {
      this.#tryPlay();
    } else if (!this.#inView && this.dataset.rState === 'playing') {
      this.#pause();
    }
  }

  async #tryPlay() {
    const { video } = this.refs;
    if (!video || this.#playPending) return;
    if (this.dataset.rState === 'playing' && !video.paused) return;

    this.prepare();
    this.#playPending = true;

    try {
      await video.play();
      this.#setState('playing');
      this.#registerPlaying();

      // The element may have left the viewport while play() settled.
      if (!this.#inView && !this.#userActivated) this.#pause();
    } catch {
      // ↗ .cursor/references/code-notes/r-media.js.md#§7
      this.#setState('blocked');
    } finally {
      this.#playPending = false;
    }
  }

  #registerPlaying() {
    playing.delete(this);
    playing.add(this);

    // FIFO-pause the oldest beyond the cap.
    while (playing.size > MAX_PLAYING) {
      const [oldest] = playing;
      if (!oldest || oldest === this) break;
      oldest.systemPause();
    }
  }

  #pause() {
    const { video } = this.refs;
    if (!video) return;

    video.pause();
    playing.delete(this);
    if (this.dataset.rState === 'playing') this.#setState('paused');
  }

  /** @param {string} state */
  #setState(state) {
    this.dataset.rState = state;
  }
}

if (!customElements.get('r-media')) {
  customElements.define('r-media', RMedia);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      for (const media of [...playing]) media.systemPause();
    } else {
      for (const media of instances) media.resync();
    }
  });

  // bfcache restore: browsers pause media entering the cache — re-evaluate.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    for (const media of instances) media.resync();
  });

  // Honour live preference flips (e.g. macOS Reduce Motion mid-session).
  matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', () => {
    for (const media of instances) {
      if (media instanceof RMedia) {
        media.systemPause();
        media.resync();
      }
    }
  });
}
